/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef, useCallback, useState } from 'react';
import searchImg from './dndimages/search.png';
import spellbookImg from './dndimages/spellbook.png';
import trashcanImg from './dndimages/trashcan.png';
import upImg from './dndimages/up-icon.png';
import downImg from './dndimages/down-icon.png';
import MarkdownHelp from './MarkdownHelp';
import Section from './model/Section';

interface AbilityPopupProps {
  name: string;
  desc: string;
  sectionName: string;
  section: Section | Ability;
  sectionOptions: string[];
  onChange: (newName: string, newDesc: string) => void;
  onClickOutside: (event: MouseEvent) => void;
  onDelete: () => void;
  onSearch: () => void;
  onSpellbook: () => void;
  showSearch: boolean;
  showSpells: boolean;
  onMoveUp: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMoveDown: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSectionChange: (newSectionName: string, section: Section | Ability, oldSectionName: string) => void;
  isFirst: boolean;
  isLast: boolean;
}

const AbilityPopup: React.FC<AbilityPopupProps> = ({
  name,
  desc,
  sectionName,
  section,
  sectionOptions,
  onChange,
  onClickOutside,
  onDelete,
  onSearch,
  onSpellbook,
  showSearch,
  showSpells,
  onMoveUp,
  onMoveDown,
  onSectionChange,
  isFirst,
  isLast,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [newName, setNewName] = useState(name);
  const [newDesc, setNewDesc] = useState(desc);
  const popupPanelRef = useRef<HTMLSpanElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    if (popupPanelRef.current && !popupPanelRef.current.contains(event.target as Node)) {
      onClickOutside(event); // let the caller determine if this should close the popup or not
    }
  }, [onClickOutside]);

  useEffect(() => {
    setNewName(name);
    setNewDesc(desc);
  }, [name, desc]);

  useEffect(() => {
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleInputBlur = () => {
    onChange(newName, newDesc);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
    onChange(event.target.value, newDesc);
  };

  const handleDescChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewDesc(event.target.value);
    onChange(newName, event.target.value);
  };

  const handleClickHelp = () => {
    setShowHelp((prev) => !prev);
  };

  return (
    <span className="ability-container" ref={popupPanelRef}>
      <span className="multi-select-panel ability-panel" id="abilityPopup">
        <button className={`image-button ${isFirst ? 'disabled' : ''}`} onClick={onMoveUp} disabled={isFirst}>
          <img src={upImg} alt="Move Up" />
        </button>
        <button className={`image-button ${isLast ? 'disabled' : ''}`} onClick={onMoveDown} disabled={isLast}>
          <img src={downImg} alt="Move Down" />
        </button>
        <span style={{ margin: '0px 0px 0px 10px' }}>Name:</span>
        <input
          style={{ width: '60%', margin: '0px 10px 0px 10px'}}
          type="text"
          value={newName}
          onChange={handleNameChange}
          onBlur={handleInputBlur}
        />
        {showSearch && (
          <button className="image-button" onClick={onSearch}>
            <img src={searchImg} alt="Search" />
          </button>
        )}
        {showSpells && (
          <button className="image-button" onClick={onSpellbook}>
            <img src={spellbookImg} alt="Search" />
          </button>
        )}
        <button className="image-button" onClick={onDelete}>
          <img src={trashcanImg} alt="Delete" />
        </button>
        <br />
        <span style={{ width: '100%' }}>
          <select className='block-selector block-header-font' style={{cursor: 'pointer'}} title='Move to other block'
                  value={sectionName} onChange={(e) => onSectionChange(e.target.value, section, sectionName)}>
            {sectionOptions.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
          </select>
          <span style={{ fontSize: 'large' }}>Description:</span>
          <button className="small-button" style={{ float: 'right', marginRight: '15px' }} onClick={handleClickHelp}>
            ? &nbsp;Format Help
          </button>
          <textarea
            style={{ width: '95%', height: '300px' }}
            value={newDesc}
            onChange={handleDescChange}
            onBlur={handleInputBlur}
          />
          {showHelp && (
            <div className="markdown-help-container">
              <MarkdownHelp />
            </div>
          )}
        </span>
      </span>
    </span>
  );
};

export default AbilityPopup;
